import { from, Observable, pipe, switchMap, UnaryFunction } from 'rxjs';

import { normalizeCallBehavior } from './normalize-call-behavior';
import { normalizeErrorReporting } from './normalize-error-reporting';

/**
 * Selects the blob out of a Fetch API Response. All errors are reported using the
 * Fetch API's Response type. A timeout Response is reported if the service doesn't
 * respond within one minute. This is the complement to `ignoreData`.
 *
 * Intended to be used with `fromFetch` and Zod.
 *
 * Example use:
 * ```
 * fromFetch('url')
 *  .pipe(selectBlob())
 *  .subscribe({
 *    next: (blob) => {
 *      // handleBlob(blob);
 *    },
 *    error: (error: Response) => {
 *       // Report error.
 *       // Okay to use `error.statusText` and `error.status`.
 *       // More details may be in the error body.
 *    },
 *  });
 * ```
 * @returns Blob Observable
 */
export function selectBlob(): UnaryFunction<
  Observable<Response>,
  Observable<Blob>
> {
  return pipe(
    normalizeCallBehavior(),
    normalizeErrorReporting(),
    switchMap((response) => from(response.blob()))
  );
}
